import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Receipt, Plus, Search, X, Printer, Send, CreditCard, Trash2, Pencil,
  Briefcase, Loader2, Link as LinkIcon, Copy, Check,
} from "lucide-react";
import { api, adminHeaders } from "@/lib/api";
import { toast } from "sonner";
import InvoiceDocument from "@/components/InvoiceDocument";

const STATUSES = ["Draft", "Sent", "Partially Paid", "Paid", "Overdue", "Cancelled"];
const FILTERS = ["All", ...STATUSES];

const TONE = {
  Paid: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  "Partially Paid": "text-amber-300 border-amber-500/30 bg-amber-500/10",
  Overdue: "text-red-300 border-red-500/30 bg-red-500/10",
  Sent: "text-blue-300 border-blue-500/30 bg-blue-500/10",
  Cancelled: "text-white/50 border-white/20 bg-white/5",
  Draft: "text-white/60 border-white/20 bg-white/5",
};
const tone = (s) => TONE[s] || "text-white/70 border-white/20 bg-white/5";

const h = () => ({ headers: adminHeaders() });
const money = (cur, n) => `${cur || "\u20aa"}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (d) => new Date(Date.now() + d * 864e5).toISOString().slice(0, 10);

const emptyItem = () => ({ description: "", qty: 1, unit_price: 0 });
const emptyInvoice = () => ({
  bill_to_name: "", bill_to_email: "", bill_to_phone: "", bill_to_address: "",
  issue_date: todayISO(), due_date: plusDaysISO(14), currency: "\u20aa",
  line_items: [emptyItem()], discount: 0, vat_rate: 0,
  notes: "", terms: "Payment due within 14 days. Thank you for choosing Ascendia.",
});

const inputCls = "w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold/60";
const labelCls = "text-[11px] uppercase tracking-[0.12em] text-muted-c mb-1 block";

// ---------------- Editor (create / edit) ----------------
function InvoiceEditor({ initial, editingId, onClose, onSaved }) {
  const [form, setForm] = useState(initial || emptyInvoice());
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm((f) => {
    const items = [...f.line_items];
    items[i] = { ...items[i], [k]: v };
    return { ...f, line_items: items };
  });
  const addItem = () => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, x) => x !== i) }));

  const cur = form.currency || "\u20aa";
  const subtotal = useMemo(
    () => form.line_items.reduce((s, li) => s + Number(li.qty || 0) * Number(li.unit_price || 0), 0),
    [form.line_items]
  );
  const taxable = Math.max(subtotal - Number(form.discount || 0), 0);
  const vatAmt = taxable * Number(form.vat_rate || 0) / 100;
  const total = taxable + vatAmt;

  const save = async () => {
    if (!form.bill_to_name.trim()) { toast.error("Add a bill-to name"); return; }
    if (!form.line_items.length || !form.line_items.some((li) => li.description.trim())) {
      toast.error("Add at least one line item"); return;
    }
    const payload = {
      ...form,
      discount: Number(form.discount || 0),
      vat_rate: Number(form.vat_rate || 0),
      line_items: form.line_items
        .filter((li) => li.description.trim())
        .map((li) => ({ description: li.description, qty: Number(li.qty || 0), unit_price: Number(li.unit_price || 0) })),
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/invoices/${editingId}`, payload, h());
        toast.success("Invoice updated");
      } else {
        await api.post("/admin/invoices", payload, h());
        toast.success("Invoice created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 py-8 px-4" onClick={onClose} data-testid="invoice-editor">
      <div className="mx-auto w-full max-w-2xl ascendia-card p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold text-white">{editingId ? "Edit invoice" : "New invoice"}</h3>
          <button onClick={onClose} className="text-muted-c hover:text-white" data-testid="invoice-editor-close"><X className="h-5 w-5" /></button>
        </div>

        {/* Bill to */}
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Bill to name *</label><input className={inputCls} value={form.bill_to_name} onChange={(e) => set("bill_to_name", e.target.value)} data-testid="inv-bill-name" placeholder="Client full name" /></div>
          <div><label className={labelCls}>Email</label><input className={inputCls} value={form.bill_to_email} onChange={(e) => set("bill_to_email", e.target.value)} data-testid="inv-bill-email" placeholder="client@email.com" /></div>
          <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.bill_to_phone} onChange={(e) => set("bill_to_phone", e.target.value)} placeholder="05x-xxx-xxxx" /></div>
          <div><label className={labelCls}>Address</label><input className={inputCls} value={form.bill_to_address} onChange={(e) => set("bill_to_address", e.target.value)} placeholder="Property / billing address" /></div>
        </div>

        {/* Dates + currency */}
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <div><label className={labelCls}>Issue date</label><input type="date" className={inputCls} value={(form.issue_date || "").slice(0, 10)} onChange={(e) => set("issue_date", e.target.value)} /></div>
          <div><label className={labelCls}>Due date</label><input type="date" className={inputCls} value={(form.due_date || "").slice(0, 10)} onChange={(e) => set("due_date", e.target.value)} /></div>
          <div><label className={labelCls}>Currency</label><input className={inputCls} value={form.currency} onChange={(e) => set("currency", e.target.value)} /></div>
        </div>

        {/* Line items */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls + " mb-0"}>Line items</label>
            <button onClick={addItem} className="inline-flex items-center gap-1.5 text-xs text-gold-soft hover:text-gold" data-testid="inv-add-item"><Plus className="h-3.5 w-3.5" /> Add line</button>
          </div>
          <div className="space-y-2">
            {form.line_items.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input className={inputCls + " col-span-6"} placeholder="Description" value={li.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`inv-item-desc-${i}`} />
                <input type="number" min="0" step="0.5" className={inputCls + " col-span-2"} placeholder="Qty" value={li.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                <input type="number" min="0" step="0.01" className={inputCls + " col-span-3"} placeholder="Unit price" value={li.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                <button onClick={() => removeItem(i)} className="col-span-1 flex justify-center text-muted-c hover:text-red-300" disabled={form.line_items.length === 1}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Discount / VAT */}
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Discount ({cur})</label><input type="number" min="0" step="0.01" className={inputCls} value={form.discount} onChange={(e) => set("discount", e.target.value)} /></div>
          <div><label className={labelCls}>VAT rate (%)</label><input type="number" min="0" step="0.5" className={inputCls} value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} /></div>
        </div>

        {/* Notes / terms */}
        <div className="mt-4 grid gap-4">
          <div><label className={labelCls}>Notes (shown to client)</label><input className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Reference, PO number, etc." /></div>
          <div><label className={labelCls}>Terms</label><textarea rows={2} className={inputCls} value={form.terms} onChange={(e) => set("terms", e.target.value)} /></div>
        </div>

        {/* Live totals */}
        <div className="mt-5 flex justify-end">
          <div className="w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-c">Subtotal</span><span className="text-white">{money(cur, subtotal)}</span></div>
            {Number(form.discount) > 0 && <div className="flex justify-between"><span className="text-muted-c">Discount</span><span className="text-white">-{money(cur, form.discount)}</span></div>}
            {Number(form.vat_rate) > 0 && <div className="flex justify-between"><span className="text-muted-c">VAT ({form.vat_rate}%)</span><span className="text-white">{money(cur, vatAmt)}</span></div>}
            <div className="flex justify-between border-t border-[color:var(--border-subtle)] pt-2 text-base font-semibold"><span className="text-white">Total</span><span className="text-gold-soft" data-testid="inv-live-total">{money(cur, total)}</span></div>
          </div>
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="rounded-lg border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-white/80 hover:bg-white/5">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-60" data-testid="inv-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editingId ? "Save changes" : "Create invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- From-booking picker ----------------
function FromJobPicker({ onClose, onCreated }) {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get("/admin/jobs", h()).then(({ data }) => setJobs(data || [])).catch(() => setJobs([]));
  }, []);
  const shown = (jobs || []).filter((j) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [(j.ref || ""), (j.client_name || ""), (j.city || ""), (j.address || ""), (j.service || "")].some((v) => String(v).toLowerCase().includes(s));
  });
  const gen = async (job) => {
    setBusy(job.id);
    try {
      await api.post(`/admin/invoices/from-job/${job.id}`, {}, h());
      toast.success(`Invoice generated from ${job.ref || "booking"}`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate invoice");
    } finally { setBusy(null); }
  };
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 py-8 px-4" onClick={onClose} data-testid="from-job-picker">
      <div className="mx-auto w-full max-w-xl ascendia-card p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-white">Generate from a booking</h3>
          <button onClick={onClose} className="text-muted-c hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-1 text-sm text-muted-c">Pre-fills line items, price and any payments already collected on the job.</p>
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-c" />
          <input className={inputCls + " pl-9"} placeholder="Search by ref, client, city, service…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="mt-4 max-h-[50vh] overflow-y-auto space-y-2">
          {jobs === null ? <p className="text-sm text-muted-c">Loading bookings…</p>
            : shown.length === 0 ? <p className="text-sm text-muted-c">No matching bookings.</p>
              : shown.map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--border-subtle)] p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="font-mono text-xs text-gold-soft">{j.ref}</span><span className="text-white font-medium truncate">{j.client_name || "—"}</span></div>
                    <p className="text-xs text-muted-c truncate">{[j.service, j.city || j.address].filter(Boolean).join(" · ") || "—"}</p>
                  </div>
                  <button onClick={() => gen(j)} disabled={busy === j.id} className="inline-flex items-center gap-1.5 rounded-lg bg-gold px-3 py-2 text-xs font-semibold text-black hover:bg-gold-soft disabled:opacity-60 shrink-0" data-testid={`gen-from-job-${j.id}`}>
                    {busy === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Receipt className="h-3.5 w-3.5" />} Generate
                  </button>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Detail drawer ----------------
function InvoiceDrawer({ inv, onClose, onChanged, onEdit }) {
  const [payAmt, setPayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("manual");
  const [payNote, setPayNote] = useState("");
  const [status, setStatus] = useState(inv.status);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const cur = inv.currency || "\u20aa";
  const link = `${window.location.origin}/invoice/${inv.public_token}`;

  useEffect(() => { setStatus(inv.status); }, [inv.status]);

  const recordPayment = async () => {
    const amt = Number(payAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/admin/invoices/${inv.id}/payment`, { amount: amt, method: payMethod, note: payNote }, h());
      toast.success("Payment recorded");
      setPayAmt(""); setPayNote("");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to record payment"); }
  };
  const changeStatus = async (s) => {
    setStatus(s);
    try { await api.post(`/admin/invoices/${inv.id}/status`, { status: s }, h()); toast.success(`Marked ${s}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to update status"); }
  };
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/send`, { origin: window.location.origin }, h());
      toast.success(data?.email_sent ? "Invoice emailed to client" : "Marked as sent (no email configured)");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete invoice ${inv.number}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/invoices/${inv.id}`, h()); toast.success("Invoice deleted"); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };
  const copyLink = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" onClick={onClose}>
      <div className="w-full max-w-3xl h-full overflow-y-auto bg-[#101011] border-l border-[color:var(--border-subtle)]" onClick={(e) => e.stopPropagation()} data-testid="invoice-drawer">
        {/* Action bar */}
        <div className="no-print sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-[color:var(--border-subtle)] bg-[#101011]/95 backdrop-blur px-5 py-3">
          <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white/80 hover:bg-white/5"><X className="h-4 w-4" /> Close</button>
          <div className="flex-1" />
          <button onClick={() => onEdit(inv)} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white/80 hover:bg-white/5" data-testid="inv-edit"><Pencil className="h-4 w-4" /> Edit</button>
          <button onClick={send} disabled={sending} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white/80 hover:bg-white/5 disabled:opacity-60" data-testid="inv-send">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send</button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-lg bg-gold px-3 py-2 text-sm font-semibold text-black hover:bg-gold-soft" data-testid="inv-print"><Printer className="h-4 w-4" /> Print</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Controls */}
          <div className="no-print grid sm:grid-cols-2 gap-4">
            {/* Status + link */}
            <div className="ascendia-card p-4">
              <label className={labelCls}>Status</label>
              <select value={status} onChange={(e) => changeStatus(e.target.value)} className={inputCls} data-testid="inv-status-select">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <label className={labelCls + " mt-3"}>Shareable link</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-xs text-white/70 truncate"><LinkIcon className="h-3.5 w-3.5 shrink-0 text-gold-soft" /><span className="truncate">{link}</span></div>
                <button onClick={copyLink} className="rounded-lg border border-[color:var(--border-subtle)] p-2 text-white/80 hover:bg-white/5">{copied ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}</button>
              </div>
              <button onClick={del} className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200"><Trash2 className="h-3.5 w-3.5" /> Delete invoice</button>
            </div>
            {/* Record payment */}
            <div className="ascendia-card p-4">
              <label className={labelCls}>Record a payment</label>
              <div className="flex items-baseline justify-between text-sm mb-2"><span className="text-muted-c">Balance due</span><span className="font-semibold" style={{ color: inv.balance > 0 ? "#E7CE93" : "#6ee7b7" }} data-testid="inv-balance">{money(cur, inv.balance)}</span></div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" min="0" step="0.01" placeholder="Amount" className={inputCls} value={payAmt} onChange={(e) => setPayAmt(e.target.value)} data-testid="inv-pay-amount" />
                <select className={inputCls} value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                  <option value="manual">Manual</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="transfer">Bank transfer</option>
                  <option value="bit">Bit</option>
                </select>
              </div>
              <input placeholder="Note (optional)" className={inputCls + " mt-2"} value={payNote} onChange={(e) => setPayNote(e.target.value)} />
              <button onClick={recordPayment} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-gold py-2 text-sm font-semibold text-black hover:bg-gold-soft" data-testid="inv-record-payment"><CreditCard className="h-4 w-4" /> Record payment</button>
            </div>
          </div>

          {/* Document preview */}
          <InvoiceDocument inv={inv} />
        </div>
      </div>
    </div>
  );
}

// ---------------- Main ----------------
export default function AdminInvoices() {
  const [data, setData] = useState(null);
  const [totals, setTotals] = useState({ count: 0, outstanding: 0, paid: 0 });
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null); // { initial, editingId } | null
  const [showJobs, setShowJobs] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter !== "All") params.status = filter;
      if (search.trim()) params.search = search.trim();
      const { data: res } = await api.get("/admin/invoices", { ...h(), params });
      setData(res.invoices || []);
      setTotals(res.totals || { count: 0, outstanding: 0, paid: 0 });
      // keep drawer in sync with freshest record
      setSelected((cur) => (cur ? (res.invoices || []).find((i) => i.id === cur.id) || null : null));
    } catch { setData([]); }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-invoices">
      {/* Header + actions */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="kicker mb-1">Billing</p>
          <h2 className="font-display text-2xl font-semibold text-white">Invoices</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowJobs(true)} className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--border-subtle)] px-4 py-2 text-sm text-white/80 hover:bg-white/5" data-testid="invoice-from-booking-btn"><Briefcase className="h-4 w-4" /> From booking</button>
          <button onClick={() => setEditor({ initial: emptyInvoice(), editingId: null })} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-black hover:bg-gold-soft" data-testid="invoice-new-btn"><Plus className="h-4 w-4" /> New invoice</button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-4">
        {[["Invoices", totals.count, ""], ["Outstanding", totals.outstanding, "\u20aa"], ["Collected", totals.paid, "\u20aa"]].map(([k, v, c]) => (
          <div key={k} className="ascendia-card p-5"><p className="text-xs uppercase tracking-wide text-muted-c">{k}</p><p className="mt-2 font-display text-2xl font-semibold text-white">{c}{Number(v).toLocaleString()}</p></div>
        ))}
      </div>

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs border ${filter === s ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`} data-testid={`invoice-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-c" />
          <input className={inputCls + " pl-9 w-64"} placeholder="Search number, name, email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="invoice-search" />
        </div>
      </div>

      {/* List */}
      {data === null ? (
        <p className="text-sm text-muted-c">Loading invoices…</p>
      ) : data.length === 0 ? (
        <div className="ascendia-card p-10 text-center"><Receipt className="h-8 w-8 text-gold-soft mx-auto" /><p className="mt-4 text-white font-medium">No invoices yet</p><p className="mt-1 text-sm text-muted-c">Create one manually or generate it from a booking.</p></div>
      ) : (
        <div className="space-y-3" data-testid="admin-invoices-list">
          {data.map((inv) => (
            <button key={inv.id} onClick={() => setSelected(inv)} className="w-full ascendia-card p-5 text-left flex flex-wrap items-center justify-between gap-4 hover:border-gold/40 transition-colors" data-testid={`invoice-row-${inv.id}`}>
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-gold-soft">{inv.number}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tone(inv.status)}`}>{inv.status}</span>
                  {inv.job_id && <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-white/50">from booking</span>}
                </div>
                <p className="mt-2 text-white font-medium">{inv.bill_to_name || "—"} <span className="text-sm text-muted-c">· issued {(inv.issue_date || "").slice(0, 10)}</span></p>
              </div>
              <div className="text-right">
                <p className="text-white font-semibold">{money(inv.currency, inv.total)}</p>
                {inv.balance > 0 ? <p className="text-xs text-gold-soft">{money(inv.currency, inv.balance)} due</p> : <p className="text-xs text-emerald-300">Paid in full</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <InvoiceDrawer inv={selected} onClose={() => setSelected(null)} onChanged={load} onEdit={(inv) => { setSelected(null); setEditor({ initial: { ...emptyInvoice(), ...inv }, editingId: inv.id }); }} />}
      {editor && <InvoiceEditor initial={editor.initial} editingId={editor.editingId} onClose={() => setEditor(null)} onSaved={load} />}
      {showJobs && <FromJobPicker onClose={() => setShowJobs(false)} onCreated={load} />}
    </div>
  );
}
